import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Archive,
  ArchiveRestore,
  ExternalLink,
  Pause,
  Pencil,
  Play,
  Plus,
  Users,
} from "lucide-react";
import {
  ApiError,
  createClub,
  listClubs,
  setClubStatus,
  updateClub,
} from "@/lib/api";
import { unscopedKey } from "@/lib/queryClient";
import { useToast } from "@/components/ui/toast";
import { Button } from "@/components/ui/Button";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import {
  AdminIconButton,
  AdminIconLink,
} from "@/components/console/AdminIconButton";
import { ClubAdminsPanel } from "@/features/super-admin/components/ClubAdminsPanel";
import { apexDomain } from "@/lib/hostKind";
import type { AdminClubRow } from "@/lib/types";

function formatErr(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "Something went wrong.";
}

const CLUBS_KEY = unscopedKey("super-admin-clubs");

// The list filter: "default" hides archived (retired clubs stay out of the
// working list); the rest are exact statuses.
type ClubsFilter = "default" | "active" | "suspended" | "archived";

const FILTER_CHIPS: Array<{ value: ClubsFilter; label: string }> = [
  { value: "default", label: "All" },
  { value: "active", label: "Active" },
  { value: "suspended", label: "Suspended" },
  { value: "archived", label: "Archived" },
];

export function ClubsPage() {
  const [composerOpen, setComposerOpen] = useState(false);
  const [filter, setFilter] = useState<ClubsFilter>("default");
  const clubsQuery = useQuery({
    // Same base key as the unfiltered list so existing prefix
    // invalidations (admin/quota mutations) refresh every filter view.
    queryKey: unscopedKey("super-admin-clubs", { filter }),
    queryFn: () => listClubs(filter === "default" ? undefined : filter),
    staleTime: 10_000,
  });

  return (
    <div data-testid="super-admin-clubs" className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-col">
          <h2 className="font-display text-base text-ink">Clubs</h2>
          <p className="text-xs text-ink-muted">
            Every club on the platform. Each lives at its own{" "}
            <span className="text-ink">subdomain</span>.
          </p>
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={() => setComposerOpen(true)}
          data-testid="super-admin-create-club"
        >
          <Plus aria-hidden /> New club
        </Button>
      </div>

      <div
        data-testid="super-admin-clubs-filter"
        className="flex items-center gap-1"
        role="group"
        aria-label="Filter clubs by status"
      >
        {FILTER_CHIPS.map((chip) => (
          <button
            key={chip.value}
            type="button"
            onClick={() => setFilter(chip.value)}
            data-testid={`super-admin-clubs-filter-${chip.value}`}
            data-active={filter === chip.value}
            className={[
              "rounded-pill border px-2 py-0.5 text-[0.6rem] font-display uppercase tracking-wider transition-colors",
              filter === chip.value
                ? "border-accent bg-accent/10 text-accent-text"
                : "border-rule bg-field text-ink-muted hover:text-ink",
            ].join(" ")}
          >
            {chip.label}
          </button>
        ))}
      </div>

      {clubsQuery.isError ? (
        <p className="text-destructive-text font-mono text-xs">
          {formatErr(clubsQuery.error)}
        </p>
      ) : clubsQuery.isPending ? (
        <p className="text-xs font-mono text-ink-muted">Loading…</p>
      ) : clubsQuery.data.length === 0 ? (
        <p
          data-testid="super-admin-clubs-empty"
          className="rounded-panel border border-rule bg-surface px-3 py-6 text-center text-xs font-mono text-ink-muted"
        >
          No clubs yet. Create the first one.
        </p>
      ) : (
        <ul className="flex flex-col gap-1">
          {clubsQuery.data.map((club) => (
            <ClubRow key={club.id} club={club} />
          ))}
        </ul>
      )}

      {composerOpen ? (
        <ClubComposer onClose={() => setComposerOpen(false)} />
      ) : null}
    </div>
  );
}

const STATUS_TOAST: Record<string, string> = {
  active: "reactivated",
  suspended: "suspended",
  archived: "archived",
};

function ClubRow({ club }: { club: AdminClubRow }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [adminsOpen, setAdminsOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const suspended = club.status === "suspended";
  const archived = club.status === "archived";
  const mut = useMutation({
    mutationFn: (next: "active" | "suspended" | "archived") =>
      setClubStatus(club.id, next),
    onSuccess: (_club, next) => {
      toast.success(`${club.name} ${STATUS_TOAST[next]}`);
      queryClient.invalidateQueries({ queryKey: CLUBS_KEY });
    },
    onError: (err) =>
      toast.error("Couldn't update club", { description: formatErr(err) }),
  });

  return (
    <li
      data-testid="super-admin-club-row"
      data-club-id={club.id}
      data-status={club.status}
      className="grid grid-cols-[1fr_auto] items-center gap-2 rounded-inset border border-rule/60 bg-elevated px-2 py-1.5 text-xs"
    >
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <Link
            to="/super-admin/clubs/$clubId"
            params={{ clubId: club.id }}
            className="truncate text-ink hover:text-accent-text hover:underline"
            data-testid="super-admin-club-open-detail"
          >
            {club.name}
          </Link>
          {suspended || archived ? (
            <span className="shrink-0 rounded-pill border border-rule bg-field px-1.5 py-0.5 text-[0.55rem] font-display uppercase tracking-wider text-ink-muted">
              {archived ? "Archived" : "Suspended"}
            </span>
          ) : null}
        </div>
        <div className="text-[0.6rem] font-mono text-ink-muted">
          {club.subdomain}.{apexDomain()}
        </div>
        <div
          data-testid="super-admin-club-counts"
          className="text-[0.6rem] font-mono text-ink-muted"
        >
          {club.team_count} {club.team_count === 1 ? "team" : "teams"} ·{" "}
          {club.member_count} {club.member_count === 1 ? "member" : "members"} ·{" "}
          {club.admin_count} {club.admin_count === 1 ? "admin" : "admins"}
        </div>
      </div>
      <div className="flex items-center gap-1">
        {club.status === "active" ? (
          // Step INTO the club: its admin console lives on the club's own
          // subdomain (there is no host club on this apex console to scope
          // an edit to). The shared *.tktic.com session carries over, and
          // the super-admin override grants club_admin capabilities there.
          // Plain anchor (new host = full document load) in a new tab so
          // the operator keeps this console. A suspended club's host 404s,
          // so the link hides with it.
          <AdminIconLink
            icon={ExternalLink}
            label={`Open ${club.name}'s admin console`}
            href={`https://${club.subdomain}.${apexDomain()}/admin/settings`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="super-admin-club-open"
          />
        ) : null}
        <AdminIconButton
          icon={Users}
          label="Manage club admins"
          onClick={() => setAdminsOpen(true)}
          data-testid="super-admin-club-admins"
        />
        <AdminIconButton
          icon={Pencil}
          label="Edit club details"
          onClick={() => setEditOpen(true)}
          data-testid="super-admin-club-edit"
        />
        {!archived ? (
          <AdminIconButton
            icon={suspended ? Play : Pause}
            label={suspended ? "Reactivate club" : "Suspend club"}
            onClick={() => mut.mutate(suspended ? "active" : "suspended")}
            disabled={mut.isPending}
            data-testid="super-admin-club-toggle"
            tone={suspended ? "accent" : "destructive"}
          />
        ) : null}
        {suspended ? (
          // Archive only FROM suspended: suspend is the "are we sure" stage;
          // archive retires the club (hidden from the default list, host off,
          // invites refused) and is the only state hard-delete accepts.
          <AdminIconButton
            icon={Archive}
            label="Archive club"
            onClick={() => mut.mutate("archived")}
            disabled={mut.isPending}
            data-testid="super-admin-club-archive"
            tone="destructive"
          />
        ) : null}
        {archived ? (
          <AdminIconButton
            icon={ArchiveRestore}
            label="Restore club"
            onClick={() => mut.mutate("active")}
            disabled={mut.isPending}
            data-testid="super-admin-club-restore"
            tone="accent"
          />
        ) : null}
      </div>
      {adminsOpen ? (
        <ConfirmDialog
          title={`Club admins — ${club.name}`}
          confirmLabel="Done"
          onConfirm={() => setAdminsOpen(false)}
          onClose={() => setAdminsOpen(false)}
          message={<ClubAdminsPanel club={club} />}
        />
      ) : null}
      {editOpen ? (
        <EditClubDialog club={club} onClose={() => setEditOpen(false)} />
      ) : null}
    </li>
  );
}

const DIALOG_FIELD =
  "rounded-control border border-rule bg-field px-2 py-1.5 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
const DIALOG_LABEL =
  "text-[0.6rem] font-display uppercase tracking-wider text-ink-muted";

// Exported for reuse on the club detail page's identity card.
export function EditClubDialog({ club, onClose }: { club: AdminClubRow; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [name, setName] = useState(club.name);
  const [slug, setSlug] = useState(club.slug);
  const [subdomain, setSubdomain] = useState(club.subdomain);
  const [error, setError] = useState<string | null>(null);
  const subdomainChanged = subdomain.trim() !== club.subdomain;

  const mut = useMutation({
    mutationFn: () =>
      updateClub(club.id, {
        name: name.trim() !== club.name ? name.trim() : undefined,
        slug: slug.trim() !== club.slug ? slug.trim() : undefined,
        subdomain: subdomainChanged ? subdomain.trim() : undefined,
      }),
    onSuccess: () => {
      toast.success("Club updated");
      queryClient.invalidateQueries({ queryKey: CLUBS_KEY });
      onClose();
    },
    onError: (err) => {
      setError(formatErr(err));
      toast.error("Couldn't update club", { description: formatErr(err) });
    },
  });

  return (
    <ConfirmDialog
      title={`Edit ${club.name}`}
      confirmLabel={mut.isPending ? "Saving…" : "Save changes"}
      confirmDisabled={mut.isPending || !name.trim() || !slug.trim() || !subdomain.trim()}
      onConfirm={() => mut.mutate()}
      onClose={onClose}
      message={
        <div className="flex flex-col gap-2">
          <label className="flex flex-col gap-1">
            <span className={DIALOG_LABEL}>Name</span>
            <input
              className={DIALOG_FIELD}
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="super-admin-edit-name"
              autoFocus
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className={DIALOG_LABEL}>Slug</span>
            <input
              className={DIALOG_FIELD}
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              data-testid="super-admin-edit-slug"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className={DIALOG_LABEL}>Subdomain</span>
            <input
              className={DIALOG_FIELD}
              value={subdomain}
              onChange={(e) => setSubdomain(e.target.value)}
              data-testid="super-admin-edit-subdomain"
            />
          </label>
          {subdomainChanged ? (
            <p
              data-testid="super-admin-edit-subdomain-warning"
              className="rounded-control border border-destructive/40 bg-destructive/5 px-2 py-1.5 text-[0.65rem] text-destructive-text"
            >
              Changing the subdomain moves the club to{" "}
              <span className="font-mono">
                {subdomain.trim()}.{apexDomain()}
              </span>
              .
              Existing links break, and the new subdomain needs its DNS /
              certificate in place before it will load. The old subdomain is
              released — another club can claim it, and you can move back to
              it only while it stays free.
            </p>
          ) : null}
          {error ? (
            <p className="text-destructive-text font-mono text-xs">{error}</p>
          ) : null}
        </div>
      }
    />
  );
}

function ClubComposer({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [subdomain, setSubdomain] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      createClub({ name, slug, subdomain: subdomain || undefined }),
    onSuccess: () => {
      toast.success("Club created");
      queryClient.invalidateQueries({ queryKey: CLUBS_KEY });
      onClose();
    },
    onError: (err) => {
      setError(formatErr(err));
      toast.error("Couldn't create club", { description: formatErr(err) });
    },
  });

  const field =
    "rounded-control border border-rule bg-field px-2 py-1.5 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
  const labelText =
    "text-[0.6rem] font-display uppercase tracking-wider text-ink-muted";

  return (
    <ConfirmDialog
      title="New club"
      confirmLabel={mut.isPending ? "Creating…" : "Create club"}
      confirmDisabled={mut.isPending || !name.trim() || !slug.trim()}
      onConfirm={() => mut.mutate()}
      onClose={onClose}
      message={
        <div className="flex flex-col gap-2">
          <label className="flex flex-col gap-1">
            <span className={labelText}>Name</span>
            <input
              className={field}
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="super-admin-club-name"
              autoFocus
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className={labelText}>Slug</span>
            <input
              className={field}
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="riverside"
              data-testid="super-admin-club-slug"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className={labelText}>Subdomain (defaults to slug)</span>
            <input
              className={field}
              value={subdomain}
              onChange={(e) => setSubdomain(e.target.value)}
              placeholder="riverside"
              data-testid="super-admin-club-subdomain"
            />
          </label>
          {error ? (
            <p className="text-destructive-text font-mono text-xs">{error}</p>
          ) : null}
        </div>
      }
    />
  );
}
